/**
 * Mobile session store — 给 shared streamDecoder 当 CoworkSessionStore 用。
 *
 * decoder（@cowork/streamDecoder）把原生 cowork SDK 消息 fold 进这个 store；
 * Session.tsx 渲染 store 里的 CortexMessage[]。和 desktop renderer 同一个
 * decoder、同一份会话模型 —— store 实例 + 视图各自一份（手机 UX 不同）。
 *
 * 结构上满足 cowork-core 的 CoworkSessionStore 接口（getState() 返回的
 * 字段是 CoworkSessionStoreState 的超集），decoder 直接拿 useSessionStore 用。
 */

import { create } from 'zustand'
import type {
  CortexMessage,
  ContentBlock,
  HITLState,
  SessionState,
  StreamingPhase,
} from '@cowork/sessionModel'

const DEFAULT_STATE: SessionState = {
  processIsRunning: false,
  streamingPhase: 'idle',
  turnStartTime: null,
  lastActivityAt: 0,
  lastMessageTime: 0,
  messages: [],
  transcriptGeneration: 0,
  todos: [],
  contextUsage: null,
  loadedTools: [],
  filesPersisted: [],
  compacting: false,
  compactionStartedAt: null,
  expectingCompact: false,
  lastCompactSummary: null,
  workflows: {},
}

export interface SessionStoreState {
  sessions: Record<string, SessionState>
  currentSessionId: string | undefined
  hitl: HITLState | null

  /** 进入一个会话：建空 session（已存在则保留），设为 current。 */
  ensureSession: (sessionId: string) => void
  /** 重新 attach 同一会话时清空 transcript（snapshot 会重新 fold）。 */
  resetSession: (sessionId: string) => void
  updateSession: (sessionId: string, patch: Partial<SessionState>) => void
  updateMessages: (sessionId: string, updater: (prev: CortexMessage[]) => CortexMessage[]) => void
  updateBlocks: (sessionId: string, messageId: string, updater: (prev: ContentBlock[]) => ContentBlock[]) => void
  setPhase: (sessionId: string, phase: StreamingPhase) => void
  stopProcess: (sessionId: string) => void
  setHitl: (hitl: HITLState | null) => void
}

function patchSession(
  s: SessionStoreState,
  sessionId: string,
  patch: Partial<SessionState>,
): Pick<SessionStoreState, 'sessions'> {
  return {
    sessions: {
      ...s.sessions,
      [sessionId]: { ...(s.sessions[sessionId] || DEFAULT_STATE), ...patch, lastActivityAt: Date.now() },
    },
  }
}

export const useSessionStore = create<SessionStoreState>((set) => ({
  sessions: {},
  currentSessionId: undefined,
  hitl: null,

  ensureSession: (sessionId) => set((s) =>
    s.sessions[sessionId]
      ? { currentSessionId: sessionId }
      : { sessions: { ...s.sessions, [sessionId]: { ...DEFAULT_STATE } }, currentSessionId: sessionId },
  ),

  resetSession: (sessionId) => set((s) => ({
    sessions: { ...s.sessions, [sessionId]: { ...DEFAULT_STATE } },
    currentSessionId: sessionId,
  })),

  updateSession: (sessionId, patch) => set((s) => patchSession(s, sessionId, patch)),

  updateMessages: (sessionId, updater) => set((s) => {
    const sess = s.sessions[sessionId] || DEFAULT_STATE
    return patchSession(s, sessionId, { messages: updater(sess.messages) })
  }),

  updateBlocks: (sessionId, messageId, updater) => set((s) => {
    const sess = s.sessions[sessionId]
    if (!sess) return {}
    return patchSession(s, sessionId, {
      messages: sess.messages.map((m) =>
        m.id === messageId ? { ...m, contentBlocks: updater(m.contentBlocks || []) } : m,
      ),
    })
  }),

  setPhase: (sessionId, phase) => set((s) => patchSession(s, sessionId, { streamingPhase: phase })),

  stopProcess: (sessionId) => set((s) => patchSession(s, sessionId, {
    processIsRunning: false,
    turnStartTime: null,
    streamingPhase: 'idle',
  })),

  setHitl: (hitl) => set({ hitl }),
}))
